import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    AlertCircle, CheckCircle2, XCircle, Award, RotateCcw, Inbox,
    ArrowLeft,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

function QuestionCard({ q, i, answer, setAnswer, review }) {
    const kind = q.kind;
    const isRight = review?.correct === true;
    return (
        <Card className="mb-5" data-testid={`quiz-question-${i}`}>
            <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className="text-xs text-neutral-500 uppercase tracking-wider">
                        {i + 1} · {q.kind.replace(/_/g, ' ')} · {q.difficulty}
                    </div>
                    {review && (isRight
                        ? <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid={`quiz-result-question-${i}-badge`}><CheckCircle2 size={12} className="mr-1" />Correct</Badge>
                        : <Badge variant="destructive" data-testid={`quiz-result-question-${i}-badge`}><XCircle size={12} className="mr-1" />Incorrect</Badge>)}
                </div>
                <div className="font-medium mb-4">{q.prompt}</div>
                {(kind === "MULTIPLE_CHOICE" || kind === "TRUE_FALSE" || kind === "IMAGE_QUESTION") && (
                    <RadioGroup value={String(answer ?? "")} onValueChange={(v) => !review && setAnswer(parseInt(v))}>
                        {q.options.map((o, j) => (
                            <div key={o.id} className="flex items-center gap-2 py-1.5">
                                <RadioGroupItem value={String(j)} id={`q${i}-o${j}`} disabled={!!review} data-testid={`q${i}-option-${j}`} />
                                <Label htmlFor={`q${i}-o${j}`} className="cursor-pointer">{o.text}</Label>
                            </div>
                        ))}
                    </RadioGroup>
                )}
                {kind === "MULTIPLE_SELECT" && (
                    <div>
                        {q.options.map((o, j) => {
                            const sel = Array.isArray(answer) && answer.includes(j);
                            return (
                                <div key={o.id} className="flex items-center gap-2 py-1.5">
                                    <Checkbox id={`q${i}-o${j}`} checked={sel} disabled={!!review}
                                        onCheckedChange={(v) => {
                                            const cur = Array.isArray(answer) ? [...answer] : [];
                                            const idx = cur.indexOf(j);
                                            if (v && idx < 0) cur.push(j);
                                            if (!v && idx >= 0) cur.splice(idx, 1);
                                            setAnswer(cur);
                                        }}
                                        data-testid={`q${i}-check-${j}`} />
                                    <Label htmlFor={`q${i}-o${j}`} className="cursor-pointer">{o.text}</Label>
                                </div>
                            );
                        })}
                    </div>
                )}
                {kind === "SEQUENCE_STEPS" && (
                    <div className="space-y-1">
                        {q.options.map((o, j) => {
                            const cur = Array.isArray(answer) ? [...answer] : q.options.map((_, k) => k);
                            const pos = cur.indexOf(j);
                            return (
                                <div key={o.id} className="flex items-center gap-2">
                                    <span className="w-6 text-neutral-500 text-sm">{pos + 1}.</span>
                                    <span className="flex-1">{o.text}</span>
                                    {!review && (
                                        <>
                                            <Button variant="ghost" size="sm" data-testid={`q${i}-up-${j}`}
                                                onClick={() => { const c = [...cur]; if (pos > 0) { [c[pos - 1], c[pos]] = [c[pos], c[pos - 1]]; setAnswer(c); } }}>↑</Button>
                                            <Button variant="ghost" size="sm" data-testid={`q${i}-down-${j}`}
                                                onClick={() => { const c = [...cur]; if (pos < c.length - 1) { [c[pos + 1], c[pos]] = [c[pos], c[pos + 1]]; setAnswer(c); } }}>↓</Button>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {kind === "MATCH_ITEMS" && (
                    <div className="space-y-2">
                        {(q.match_pairs || []).map((p, j) => (
                            <div key={j} className="grid grid-cols-2 gap-3 items-center">
                                <div className="text-sm">{p.left}</div>
                                <input className="bg-neutral-900 border border-neutral-800 rounded px-3 py-1.5 text-sm"
                                    disabled={!!review}
                                    value={(answer && answer[p.left]) || ""}
                                    onChange={(e) => setAnswer({ ...(answer || {}), [p.left]: e.target.value })}
                                    data-testid={`q${i}-match-${j}`} />
                            </div>
                        ))}
                    </div>
                )}
                {review && q.explanation && (
                    <div className="mt-4 p-3 rounded bg-neutral-900 border border-neutral-800 text-sm text-neutral-300" data-testid={`quiz-result-question-${i}-explanation`}>
                        <span className="font-semibold text-neutral-100">Why: </span>{q.explanation}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ShellHeader({ title, nav }) {
    return (
        <header className="border-b border-neutral-800">
            <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                <div className="font-semibold">{title}</div>
                <Button size="sm" variant="ghost" onClick={() => nav("/learn")} data-testid="btn-exit-quiz">Exit</Button>
            </div>
        </header>
    );
}

export default function QuizTaking() {
    const { aid } = useParams();
    const nav = useNavigate();
    const [assignment, setAssignment] = useState(null);
    const [quiz, setQuiz] = useState(null);
    const [answers, setAnswers] = useState({}); // {qid: value}
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null); // string
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);      // {kind: 'empty'|'error', message}

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const a = await api.get(`/assignments/${aid}`);
                if (cancelled) return;
                setAssignment(a.data);
                try {
                    const q = await api.get(`/modules/${a.data.module_id}/quiz`);
                    if (cancelled) return;
                    setQuiz(q.data);
                } catch (qe) {
                    if (cancelled) return;
                    const status = qe?.response?.status;
                    if (status === 404 || status === 409) {
                        setError({ kind: "empty" });
                    } else {
                        setError({ kind: "error", message: formatApiError(qe) });
                    }
                }
            } catch (ae) {
                if (cancelled) return;
                const status = ae?.response?.status;
                if (status === 404) {
                    setError({ kind: "error", message: "Assignment not found." });
                } else {
                    setError({ kind: "error", message: formatApiError(ae) });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [aid]);

    async function submit() {
        setSubmitting(true);
        setSubmitError(null);
        const payload = (quiz.questions || []).map((q) => ({ question_id: q.id, given: answers[q.id] }));
        try {
            const r = await api.post(
                `/assignments/${aid}/quiz/attempt`,
                { answers: payload },
                { timeout: 30000 },
            );
            if (r?.data && typeof r.data === "object" && "score" in r.data) {
                setResult(r.data);
                // Scroll to top so learner sees the score card
                try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
            } else {
                setSubmitError("Unexpected response from server.");
            }
        } catch (e) {
            setSubmitError(formatApiError(e) || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    function retake() {
        setResult(null);
        setAnswers({});
        setSubmitError(null);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    }

    const title = assignment ? `Knowledge Check · ${assignment.module_name}` : "Knowledge Check";

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="quiz-page-container">
                <ShellHeader title={title} nav={nav} />
                <main className="max-w-3xl mx-auto px-6 py-16">
                    <div className="flex items-center gap-3 text-neutral-400" data-testid="quiz-loading">
                        <div className="h-4 w-4 rounded-full border-2 border-neutral-600 border-t-neutral-100 animate-spin" />
                        <span>Loading knowledge check…</span>
                    </div>
                </main>
            </div>
        );
    }

    if (error?.kind === "empty") {
        return (
            <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="quiz-page-container">
                <ShellHeader title={title} nav={nav} />
                <main className="max-w-3xl mx-auto px-6 py-16">
                    <Card data-testid="quiz-empty-state">
                        <CardContent className="p-8 flex flex-col items-center text-center gap-3">
                            <Inbox size={40} className="text-neutral-500" />
                            <div className="text-lg font-semibold">Knowledge check not yet available</div>
                            <div className="text-sm text-neutral-400 max-w-md">
                                This module doesn't have an approved knowledge check yet.
                                Please contact your administrator.
                            </div>
                            <Button className="mt-3" variant="outline" onClick={() => nav("/learn")} data-testid="btn-back-to-learn">
                                Back to learning
                            </Button>
                        </CardContent>
                    </Card>
                </main>
            </div>
        );
    }

    if (error?.kind === "error") {
        return (
            <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="quiz-page-container">
                <ShellHeader title={title} nav={nav} />
                <main className="max-w-3xl mx-auto px-6 py-16">
                    <div
                        className="flex items-start gap-3 rounded border border-red-900 bg-red-950/30 p-4 text-sm text-red-200"
                        data-testid="quiz-error"
                    >
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold text-red-100">Couldn't load the knowledge check</div>
                            <div className="mt-1 text-red-200/80">{error.message || "Unexpected error."}</div>
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <Button variant="outline" onClick={() => nav("/learn")} data-testid="btn-back-to-learn">
                            Back to learning
                        </Button>
                        <Button onClick={() => window.location.reload()} data-testid="btn-retry-quiz">
                            Retry
                        </Button>
                    </div>
                </main>
            </div>
        );
    }

    const questions = quiz?.questions || [];
    const reviewByQ = {};
    if (result?.results) result.results.forEach(r => { reviewByQ[r.question_id] = r; });

    return (
        <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="quiz-page-container">
            <ShellHeader title={title} nav={nav} />
            <main className="max-w-3xl mx-auto px-6 py-8">
                {result && (
                    <Card
                        className={`mb-6 ${result.passed ? 'border-emerald-700 bg-emerald-950/30' : 'border-red-900 bg-red-950/30'}`}
                        data-testid="quiz-result-card"
                    >
                        <CardContent className="p-6 flex flex-wrap items-center gap-4">
                            {result.passed
                                ? <Award className="text-emerald-500" size={40} />
                                : <XCircle className="text-red-500" size={40} />}
                            <div className="flex-1 min-w-[220px]">
                                <div className="text-xl font-semibold" data-testid="quiz-result-score">
                                    {result.passed ? `Passed — ${result.score}%` : `Not passed — ${result.score}%`}
                                </div>
                                <div className="text-sm text-neutral-400 mt-0.5">
                                    {result.passed
                                        ? "Great work. Certificate ready for download."
                                        : `You need ${quiz.passing_score}% to pass.`}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {result.passed && (
                                    <Button
                                        onClick={() => nav("/learn/certificates")}
                                        data-testid="btn-view-cert"
                                    >
                                        View certificate
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={retake}
                                    data-testid="quiz-retake-button"
                                >
                                    <RotateCcw size={14} className="mr-1" />Retake
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={() => nav("/learn")}
                                    data-testid="quiz-return-dashboard-button"
                                >
                                    <ArrowLeft size={14} className="mr-1" />Return to dashboard
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {submitError && !result && (
                    <div
                        className="mb-5 flex items-start gap-3 rounded border border-red-900 bg-red-950/40 p-4 text-sm text-red-200"
                        data-testid="quiz-submit-error"
                    >
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="font-semibold text-red-100">Could not submit. Please try again.</div>
                            <div className="mt-1 text-red-200/80">{submitError}</div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={submit}
                            disabled={submitting}
                            data-testid="quiz-submit-retry-button"
                        >
                            Retry
                        </Button>
                    </div>
                )}

                {questions.map((q, i) => (
                    <QuestionCard key={q.id} q={q} i={i}
                        answer={answers[q.id]}
                        setAnswer={(v) => setAnswers({ ...answers, [q.id]: v })}
                        review={reviewByQ[q.id]}
                    />
                ))}

                {!result && (
                    <div className="flex justify-end sticky bottom-4">
                        <Button
                            size="lg"
                            onClick={submit}
                            disabled={submitting}
                            data-testid="quiz-submit-button"
                        >
                            {submitting
                                ? <span data-testid="quiz-scoring">Scoring…</span>
                                : "Submit answers"}
                        </Button>
                    </div>
                )}
            </main>
        </div>
    );
}
